// SPDX-License-Identifier: AGPL-3.0-or-later

import {VOICE_ROOM_THEME_IDS} from '@fluxer/constants/src/VoiceRoomThemes';
import {EXPERIMENT_BUCKET_RESOLUTION, experimentBucket} from '@fluxer/schema/src/domains/experiment/ExperimentBucket';
import {z} from 'zod';

const VOICE_ROOM_MAX_TARGETED_USERS = 1000;
const DEFAULT_VOICE_ROOM_SALT = 'voice-room-v1';
// Unlike the other rollouts (default 0), a stored config from before the rollout fields keeps reaching everyone.
const DEFAULT_VOICE_ROOM_ROLLOUT_BASIS_POINTS = EXPERIMENT_BUCKET_RESOLUTION;

const VOICE_ROOM_SALT_PATTERN = /^[\x20-\x7e]+$/u;

const VoiceRoomTargetedUserIdsSchema = z.array(z.string().regex(/^\d{1,20}$/u)).max(VOICE_ROOM_MAX_TARGETED_USERS);

const voiceRoomRolloutFields = {
	rollout_basis_points: z.number().int().min(0).max(EXPERIMENT_BUCKET_RESOLUTION),
	rollout_salt: z.string().trim().min(1).max(64).regex(VOICE_ROOM_SALT_PATTERN),
	included_user_ids: VoiceRoomTargetedUserIdsSchema,
	excluded_user_ids: VoiceRoomTargetedUserIdsSchema,
};

const ROLLOUT_BASIS_POINTS_DESCRIPTION =
	'Share of accounts the switches reach, in basis points (0-10000); a feature gate, not a privacy boundary';
const ROLLOUT_SALT_DESCRIPTION = 'Salt of the sampling hash';
const INCLUDED_USER_IDS_DESCRIPTION = `Accounts the switches always reach, up to ${VOICE_ROOM_MAX_TARGETED_USERS}`;
const EXCLUDED_USER_IDS_DESCRIPTION = `Accounts the switches never reach, up to ${VOICE_ROOM_MAX_TARGETED_USERS}; wins over every other rule`;

export const VoiceRoomConfigSchema = z.object({
	rooms_enabled: z.boolean().default(false).describe('Whether voice channels with a room theme open the 3D room view'),
	games_enabled: z.boolean().default(false).describe('Whether table games are playable; needs rooms_enabled'),
	room_cameras_enabled: z
		.boolean()
		.default(false)
		.describe('Whether cameras show on avatars inside the 3D room; needs rooms_enabled'),
	room_avatars_enabled: z
		.boolean()
		.default(false)
		.describe('Whether people show as 3D characters inside the room; needs rooms_enabled'),
	room_walk_enabled: z
		.boolean()
		.default(false)
		.describe('Whether characters walk around the room with WASD; needs rooms_enabled and room_avatars_enabled'),
	room_watch_enabled: z
		.boolean()
		.default(false)
		.describe('Whether people play a shared YouTube video on the room TV; needs rooms_enabled'),
	room_watch_enabled_at: z
		.number()
		.int()
		.nonnegative()
		.optional()
		.describe('Server time (ms) room TV videos last came on; set by the server, absent until then'),
	room_stage_enabled: z
		.boolean()
		.default(false)
		.describe(
			'Whether bots with MANAGE_ROOM_STAGE run the 3D room stage (TV board, host character, effects); needs rooms_enabled',
		),
	default_room_theme: z
		.enum(VOICE_ROOM_THEME_IDS)
		.nullable()
		.default(null)
		.describe('Default room theme for voice channels that inherit it; null disables the default'),
	rollout_basis_points: voiceRoomRolloutFields.rollout_basis_points
		.default(DEFAULT_VOICE_ROOM_ROLLOUT_BASIS_POINTS)
		.describe(ROLLOUT_BASIS_POINTS_DESCRIPTION),
	rollout_salt: voiceRoomRolloutFields.rollout_salt.default(DEFAULT_VOICE_ROOM_SALT).describe(ROLLOUT_SALT_DESCRIPTION),
	included_user_ids: voiceRoomRolloutFields.included_user_ids.default([]).describe(INCLUDED_USER_IDS_DESCRIPTION),
	excluded_user_ids: voiceRoomRolloutFields.excluded_user_ids.default([]).describe(EXCLUDED_USER_IDS_DESCRIPTION),
});

type VoiceRoomOptionalField =
	| 'room_avatars_enabled'
	| 'room_walk_enabled'
	| 'room_watch_enabled'
	| 'room_stage_enabled'
	| keyof typeof voiceRoomRolloutFields;

// room_avatars_enabled, room_walk_enabled, room_watch_enabled, room_stage_enabled and the rollout fields are optional in the type so configs
// built by hand before they existed still type-check; parsing always fills them in. room_watch_enabled_at stays absent
// until the server sets it.
export type VoiceRoomConfig = Omit<z.infer<typeof VoiceRoomConfigSchema>, VoiceRoomOptionalField> &
	Partial<Pick<z.infer<typeof VoiceRoomConfigSchema>, VoiceRoomOptionalField>>;

export const DEFAULT_VOICE_ROOM_CONFIG: VoiceRoomConfig = VoiceRoomConfigSchema.parse({});

export const VoiceRoomConfigUpdateRequest = z
	.object({
		rooms_enabled: z.boolean(),
		games_enabled: z.boolean(),
		room_cameras_enabled: z.boolean(),
		room_avatars_enabled: z.boolean(),
		room_walk_enabled: z.boolean(),
		room_watch_enabled: z.boolean(),
		room_stage_enabled: z.boolean(),
		default_room_theme: z.enum(VOICE_ROOM_THEME_IDS).nullable(),
		...voiceRoomRolloutFields,
	})
	.partial();

export type VoiceRoomConfigUpdateRequest = z.infer<typeof VoiceRoomConfigUpdateRequest>;

export const VoiceRoomConfigResponse = VoiceRoomConfigSchema.extend({
	room_avatars_enabled: z
		.boolean()
		.optional()
		.describe('Whether people show as 3D characters inside the room; needs rooms_enabled; absent reads false'),
	room_walk_enabled: z
		.boolean()
		.optional()
		.describe(
			'Whether characters walk around the room with WASD; needs rooms_enabled and room_avatars_enabled; absent reads false',
		),
	room_watch_enabled: z
		.boolean()
		.optional()
		.describe('Whether people play a shared YouTube video on the room TV; needs rooms_enabled; absent reads false'),
	room_stage_enabled: z
		.boolean()
		.optional()
		.describe(
			'Whether bots with MANAGE_ROOM_STAGE run the 3D room stage (TV board, host character, effects); needs rooms_enabled; absent reads false',
		),
	rollout_basis_points: voiceRoomRolloutFields.rollout_basis_points
		.optional()
		.describe(`${ROLLOUT_BASIS_POINTS_DESCRIPTION}; absent reads 10000`),
	rollout_salt: voiceRoomRolloutFields.rollout_salt
		.optional()
		.describe(`${ROLLOUT_SALT_DESCRIPTION}; absent reads ${DEFAULT_VOICE_ROOM_SALT}`),
	included_user_ids: voiceRoomRolloutFields.included_user_ids
		.optional()
		.describe(`${INCLUDED_USER_IDS_DESCRIPTION}; absent reads empty`),
	excluded_user_ids: voiceRoomRolloutFields.excluded_user_ids
		.optional()
		.describe(`${EXCLUDED_USER_IDS_DESCRIPTION}; absent reads empty`),
});

export type VoiceRoomConfigResponse = z.infer<typeof VoiceRoomConfigResponse>;

export const VoiceRoomAssignmentResponse = z.object({
	rooms: z.boolean().describe('3D room view is available'),
	games: z.boolean().describe('Table games are available'),
	// Optional on the wire: a server that predates the field still parses; readers treat absent as false.
	cameras: z.boolean().optional().describe('Cameras on avatars inside the 3D room are available'),
	avatars: z.boolean().optional().describe('3D characters inside the room are available'),
	walk: z.boolean().optional().describe('Characters can walk around the room'),
	watch: z.boolean().optional().describe('People can play a shared YouTube video on the room TV'),
	stage: z.boolean().optional().describe('The 3D room shows the stage bots run (TV board, host character, effects)'),
	watch_since: z
		.number()
		.int()
		.nonnegative()
		.optional()
		.describe('Server time (ms) room TV videos last came on: a video written before it is stale; absent: none is'),
	default_room_theme: z
		.string()
		.nullable()
		.optional()
		.describe('Default room theme for voice channels that inherit it; absent or null disables the default'),
});

export type VoiceRoomAssignmentResponse = z.infer<typeof VoiceRoomAssignmentResponse>;
export type VoiceRoomAssignment = Required<Omit<VoiceRoomAssignmentResponse, 'watch_since'>> &
	Pick<VoiceRoomAssignmentResponse, 'watch_since'>;

export const INERT_VOICE_ROOM_ASSIGNMENT = {
	rooms: false,
	games: false,
	cameras: false,
	avatars: false,
	walk: false,
	watch: false,
	stage: false,
	default_room_theme: null,
} as const;

// The Fluxer rollout order (resolvePlutoniumPageAssignment): excluded, then included, then the sampled share.
function rolloutReaches(config: VoiceRoomConfig, userId: string): boolean {
	if ((config.excluded_user_ids ?? []).includes(userId)) return false;
	if ((config.included_user_ids ?? []).includes(userId)) return true;
	return (
		experimentBucket(userId, config.rollout_salt ?? DEFAULT_VOICE_ROOM_SALT) <
		(config.rollout_basis_points ?? DEFAULT_VOICE_ROOM_ROLLOUT_BASIS_POINTS)
	);
}

// userId: the account a decision is for; outside the rollout it gets everything as if rooms were off. null: a decision
// for no one account (timers, cleanup, the watch stamp), which reads the instance switches alone.
export function resolveVoiceRoomAssignment(config: VoiceRoomConfig, userId: string | null): VoiceRoomAssignment {
	const rooms = config.rooms_enabled && (userId === null || rolloutReaches(config, userId));
	const watch = rooms && (config.room_watch_enabled ?? false);
	return {
		rooms,
		games: rooms && config.games_enabled,
		cameras: rooms && config.room_cameras_enabled,
		avatars: rooms && (config.room_avatars_enabled ?? false),
		walk: rooms && (config.room_avatars_enabled ?? false) && (config.room_walk_enabled ?? false),
		watch,
		stage: rooms && (config.room_stage_enabled ?? false),
		...(watch && config.room_watch_enabled_at !== undefined ? {watch_since: config.room_watch_enabled_at} : {}),
		default_room_theme: config.default_room_theme,
	};
}

// Room TV videos coming on (the switch, or rooms with it) date the videos written before as stale: the switch off is
// the way to end them all, and they live only in LiveKit attributes nobody else clears.
export function stampRoomWatchEnabledAt(
	current: VoiceRoomConfig,
	next: VoiceRoomConfig,
	nowMs: number,
): VoiceRoomConfig {
	return resolveVoiceRoomAssignment(next, null).watch && !resolveVoiceRoomAssignment(current, null).watch
		? {...next, room_watch_enabled_at: nowMs}
		: next;
}
